import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import Button from 'carbon-react/lib/components/button';
import Form from 'carbon-react/lib/components/form';

import { AnyError } from '@futrli/errors';
import { FormattedMessage } from '@futrli/localisation';
import { getOrganisationDetailUrl, markAsStale } from '@futrli/shared-services';

import { updateOrganisationOwnership } from '../../../core/api';
import { trackCTA, trackViewModal } from '../../../core/tracker';
import { BillingProduct } from '../../../core/types';
import { ActivationEvent } from './activate-product-sidebar';

interface Props {
  workspaceId: string;
  organisationId: string;
  product: BillingProduct;
  onLoading: (isLoading: boolean) => void;
  onClose: (event: ActivationEvent) => void;
  onError: (error: AnyError) => void;
}

/**
 * Renders an informative message to non workspace owners.
 * @param {Props} props
 * @returns {JSX.Element}
 */
export const NonWorkspaceOwnerDialogContent: React.FC<Props> = ({
  workspaceId,
  organisationId,
  product,
  onLoading,
  onClose,
  onError
}: Props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleMigrateOrgToOwnWorkspace = async (event: ActivationEvent) => {
    event.preventDefault();
    const CTAText = 'Create my workspace';

    try {
      trackCTA(CTAText, CTAText);

      const { workspace_id: workspId, id: orgId } = await updateOrganisationOwnership(workspaceId, organisationId);

      dispatch(markAsStale('Organisation'));
      dispatch(markAsStale('Workspace'));

      navigate(getOrganisationDetailUrl(workspId, orgId));
    } catch (error) {
      onError(error);
    }
  };

  React.useEffect(() => {
    trackViewModal(`${product.name} Pro non-workspace owner - upgrade request`);
    onLoading(false);
  }, [onLoading, product]);

  return (
    <Form
      stickyFooter
      leftSideButtons={
        <Button onClick={onClose} buttonType="secondary">
          <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
        </Button>
      }
      saveButton={
        <Button buttonType="primary" onClick={handleMigrateOrgToOwnWorkspace}>
          <FormattedMessage id="billing.create_workspace" defaultMessage="Create my workspace" />
        </Button>
      }
      onSubmit={e => e.preventDefault()}
    >
      <div>
        <p>
          <FormattedMessage
            id="billing.change_organisation_ownership_message"
            defaultMessage={`
                As an invitee to Predict we need to make some changes to allow you to become the bill payer for this organisation.
                We’ll set you up with your own workspace, meaning you’ll become the bill payer and you’ll be able to control who has access to this organisation.
              `}
          />
        </p>
        <br />
        <p>
          <FormattedMessage
            id="billing.all_users_having_access_will_be_granted_the_same_access"
            defaultMessage={`
                All users who currently have access will be granted the same access in your workspace and you’ll
                be able to review and change this in the Settings area if you wish.
              `}
          />
        </p>
      </div>
    </Form>
  );
};

export function trackModaUpgradeRequest(
  productName: string,
  setIsTrackedUpgradeRequest: React.Dispatch<React.SetStateAction<boolean>>
) {
  return (event: React.MouseEvent) => {
    event.preventDefault();
    const CTAText = `I'd like to upgrade ${productName}`;

    trackCTA(CTAText, CTAText);

    setIsTrackedUpgradeRequest(true);
  };
}
